// client/src/components/bank/VatCreditPastBids.js
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { formatInterestRate, formatCurrencyWithSymbol } from '../utils/formatUtils';
import { useResponsive } from '../../hooks/useResponsive';
import { Files } from 'lucide-react';

const VatCreditPastBids = () => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const location = useLocation();
  const [bids, setBids] = useState([]);
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'all-past');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exportLoading, setExportLoading] = useState({ excel: false, pdf: false });

  // Helper function to safely get allocation amount
  const getAllocationAmount = (bid) => {
    if (bid.allocation && typeof bid.allocation.amount !== 'undefined') {
      return bid.allocation.amount;
    } else if (typeof bid.allocatedAmount !== 'undefined') {
      return bid.allocatedAmount;
    } else {
      return 0;
    }
  };

  useEffect(() => {
    const fetchBids = async () => {
      try {
        const res = await axios.get('/api/vat-credit-bids/bank');

        // Filter out bids with null requests
        const validBids = (res.data.bids || []).filter(bid => bid.request !== null);

        setBids(validBids);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching VAT credit bids:');
        setError(t('common.errors.fetchBids'));
        setLoading(false);
      }
    };

    fetchBids();
  }, [t]);

  // Derived synchronously from bids + activeTab so the table never flashes
  // an intermediate (unfiltered) list while a separate effect catches up.
  const filteredBids = useMemo(() => {
    if (activeTab === 'all-past') {
      return bids;
    }
    return bids.filter(bid => bid.status === activeTab);
  }, [activeTab, bids]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleExport = async (format) => {
    if (!filteredBids || filteredBids.length === 0) {
      alert(t('bids.noDataToExport', { defaultValue: 'No data available for export' }));
      return;
    }

    setExportLoading(prev => ({ ...prev, [format]: true }));

    try {
      const response = await axios.get(`/api/vat-credit-bids/export/${format}`, {
        params: { status: activeTab !== 'all-past' ? activeTab : undefined },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Get filename from response header or create default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Past_VAT_Credit_Bids_${activeTab}_${Date.now()}.${format}`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error(`${format.toUpperCase()} export error:`, err);
      alert(t('common.error.exportFailed', {
        format: format.toUpperCase(),
        defaultValue: `Failed to export ${format.toUpperCase()} file. Please try again.`
      }));
    } finally {
      setExportLoading(prev => ({ ...prev, [format]: false }));
    }
  };

  // Mobile Card Component
  const MobileVatCreditPastBidCard = ({ bid }) => (
    <div className="mobile-record-card">
      <div className="mobile-card-header">
        <div className="mobile-card-title">
          {bid.request?.requestId ? `#${bid.request.requestId}` : t('bids.notAvailable')}
        </div>
        <div className="mobile-card-status">
          <span className={`status status-${bid.status}`}>
            {t(`status.${bid.status}`)}
          </span>
        </div>
      </div>

      <div className="mobile-card-body">
        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('requests.company')}</span>
          <span className="mobile-card-value">{bid.request?.company?.name || t('bids.notAvailable')}</span>
        </div>

        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('vatCredit.requestAmount')}</span>
          <span className="mobile-card-value">
            {bid.request?.amount ? formatCurrencyWithSymbol(bid.request.amount, bid.request?.currency || 'EUR') : t('bids.notAvailable')}
          </span>
        </div>

        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('vatCredit.bidAmount')}</span>
          <span className="mobile-card-value">{formatCurrencyWithSymbol(bid.desiredAmount, bid.request?.currency || 'EUR')}</span>
        </div>

        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('bids.allocation')}</span>
          <span className="mobile-card-value">
            {bid.status === 'approved'
              ? formatCurrencyWithSymbol(getAllocationAmount(bid), bid.request?.currency || 'EUR')
              : formatCurrencyWithSymbol(0, bid.request?.currency || 'EUR')}
          </span>
        </div>

        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('bids.interestRate')}</span>
          <span className="mobile-card-value">{formatInterestRate(bid.interestRate, bid.rateType, bid.baseRate)}</span>
        </div>

        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('vatCredit.bankIndicativeDays')}</span>
          <span className="mobile-card-value">{bid.indicativeDays} {t('common.days')}</span>
        </div>
      </div>

      <div className="mobile-card-footer">
        <div className="mobile-card-date">
          {new Date(bid.createdAt).toLocaleDateString()}
        </div>
        <div className="mobile-card-action">
          <Link
            to={`/bank/vat-credit/bids/${bid.id}`}
            className="view-btn"
          >
            {t('common.view')}
          </Link>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <div className="loading">{t('common.loadingBids')}</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="past-bids company-dashboard">
      <div className="tab-container">
        <div className="tabs">
          <div
            className={`tab ${activeTab === 'all-past' ? 'active' : ''}`}
            onClick={() => handleTabChange('all-past')}
          >
            {t('bids.allPast')}
          </div>
          <div
            className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => handleTabChange('pending')}
          >
            {t('bids.pending')}
          </div>
          <div
            className={`tab ${activeTab === 'approved' ? 'active' : ''}`}
            onClick={() => handleTabChange('approved')}
          >
            {t('status.completedPlural')}
          </div>
          <div
            className={`tab ${activeTab === 'rejected' ? 'active' : ''}`}
            onClick={() => handleTabChange('rejected')}
          >
            {t('status.rejectedPlural')}
          </div>
          <div
            className={`tab ${activeTab === 'cancelled' ? 'active' : ''}`}
            onClick={() => handleTabChange('cancelled')}
          >
            {t('status.cancelledPlural')}
          </div>
        </div>
      </div>

      <div className={`card ${isMobile ? 'mobile-card-view' : ''}`}>
        <div className="card-title">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Files className="card-title-icon" size={20} />
            <span>{t('vatCredit.pastBids')}</span>
          </div>

          {/* Export Actions */}
          {filteredBids.length > 0 && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => handleExport('excel')}
                disabled={exportLoading.excel}
                className="btn btn-primary"
                style={{
                  fontSize: '12px',
                  padding: '2px 14px',
                  height: '22px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  minWidth: '130px'
                }}
              >
                {exportLoading.excel ? (
                  <>
                    <div className="spinner" style={{ width: '10px', height: '10px' }}></div>
                    {t('common.exporting', { defaultValue: 'Exporting...' })}
                  </>
                ) : (
                  <>
                    {t('common.exportExcel', { defaultValue: 'Export Excel' })}
                  </>
                )}
              </button>
              <button
                onClick={() => handleExport('pdf')}
                disabled={exportLoading.pdf}
                className="btn btn-secondary"
                style={{
                  fontSize: '12px',
                  padding: '2px 14px !important',
                  height: '22px !important',
                  minHeight: '22px !important',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  minWidth: '130px'
                }}
              >
                {exportLoading.pdf ? (
                  <>
                    <div className="spinner" style={{ width: '10px', height: '10px' }}></div>
                    {t('common.exporting', { defaultValue: 'Exporting...' })}
                  </>
                ) : (
                  <>
                    {t('common.exportPdf', { defaultValue: 'Export PDF' })}
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Desktop Table */}
        {!isMobile && (
          <table>
            <thead>
              <tr>
                <th>{t('requests.requestId')}</th>
                <th>{t('requests.company')}</th>
                <th>{t('vatCredit.requestAmount')}</th>
                <th>{t('vatCredit.bidAmount')}</th>
                <th>{t('bids.allocation')}</th>
                <th>{t('bids.interestRate')}</th>
                <th>{t('vatCredit.indicativeDaysShort')}</th>
                <th>{t('bids.submissionDate')}</th>
                <th>{t('bids.status')}</th>
                <th>{t('common.action')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredBids.length > 0 ? (
                filteredBids.map(bid => (
                  <tr key={bid.id}>
                    <td>{bid.request?.requestId ? `#${bid.request.requestId}` : t('bids.notAvailable')}</td>
                    <td>{bid.request?.company?.name || t('bids.notAvailable')}</td>
                    <td>{bid.request?.amount ? formatCurrencyWithSymbol(bid.request.amount, bid.request?.currency || 'EUR') : t('bids.notAvailable')}</td>
                    <td>{formatCurrencyWithSymbol(bid.desiredAmount, bid.request?.currency || 'EUR')}</td>
                    <td>
                      {bid.status === 'approved'
                        ? formatCurrencyWithSymbol(getAllocationAmount(bid), bid.request?.currency || 'EUR')
                        : formatCurrencyWithSymbol(0, bid.request?.currency || 'EUR')}
                    </td>
                    <td>{formatInterestRate(bid.interestRate, bid.rateType, bid.baseRate)}</td>
                    <td>{bid.indicativeDays}</td>
                    <td style={{ fontSize: '0.9em' }}>{new Date(bid.createdAt).toLocaleDateString()}</td>
                    <td>
                      <span
                        className={`status status-${bid.status}`}
                        style={{ fontSize: '10px', padding: '3px 7px', lineHeight: '1.4', border: '1px solid transparent' }}
                      >
                        {t(`status.${bid.status}`)}
                      </span>
                    </td>
                    <td>
                      <Link
                        to={`/bank/vat-credit/bids/${bid.id}`}
                        className="view-btn"
                        style={{ paddingLeft: '8.2px', paddingRight: '8.2px', fontSize: '13px' }}
                      >
                        {t('common.view')}
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10">{t('bids.noPastBidsFound')}</td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {/* Mobile Cards - Solo su mobile */}
        {isMobile && (
          <div className="mobile-cards-container">
            {filteredBids.length > 0 ? (
              filteredBids.map(bid => (
                <MobileVatCreditPastBidCard key={bid.id} bid={bid} />
              ))
            ) : (
              <div className="mobile-record-card mobile-empty-card">
                {t('bids.noPastBidsFound')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VatCreditPastBids;
